
import { indexedDbUtils } from "../storage/indexed-db-utils";
import { TranslationCacheItem } from "./types";
import { Log } from "../log";

// 文件级别log
const fileLog = new Log(false, 'translationCache_file');
// fileLog.pause = true;

const CACHE_NAMESPACE = 'translation_cache';
let cacheStore: Awaited<ReturnType<typeof indexedDbUtils.getIndexedDbStore<Record<string, TranslationCacheItem>>>> | null = null;

/**
 * 初始化缓存
 */
async function initCache() {
  const fnLog = fileLog.sub(false, 'initCache_fn');
  fnLog.pause = true;

  if (cacheStore) {
    fnLog.log('cache already initialized');
    return cacheStore;
  }

  fnLog.log('initializing cache');
  cacheStore = await indexedDbUtils.getIndexedDbStore<Record<string, TranslationCacheItem>>(
    CACHE_NAMESPACE,
    {
      defData: {},
      mergeDefData: false,
    }
  );

  fnLog.log('cache initialized', cacheStore);
  return cacheStore;
}

/**
 * 生成缓存key
 */
function generateCacheKey(originalText: string, sourceLang: string, targetLang: string): string {
  const fnLog = fileLog.sub(false, 'generateCacheKey_fn');
  fnLog.pause = true;

  // 简单hash函数
  const hash = Array.from(originalText).reduce((hash, char) => {
    return ((hash << 5) - hash) + char.charCodeAt(0);
  }, 0);

  const key = `${sourceLang}_${targetLang}_${hash}`;
  fnLog.log('generated cache key', { originalText, sourceLang, targetLang, key });
  return key;
}

/**
 * 从缓存获取翻译
 */
async function getFromCache(
  originalText: string,
  sourceLang: string,
  targetLang: string
): Promise<TranslationCacheItem | null> {
  const fnLog = fileLog.sub(false, 'getFromCache_fn');
  fnLog.pause = true;

  fnLog.log('get from cache', { originalText, sourceLang, targetLang });

  const store = await initCache();
  const key = generateCacheKey(originalText, sourceLang, targetLang);

  const cached = store.store.read<TranslationCacheItem>(key);

  if (cached) {
    fnLog.log('cache hit', cached);
    return cached;
  } else {
    fnLog.log('cache miss');
    return null;
  }
}

/**
 * 保存翻译到缓存
 */
async function saveToCache(
  originalText: string,
  translatedText: string,
  sourceLang: string,
  targetLang: string
): Promise<void> {
  const fnLog = fileLog.sub(false, 'saveToCache_fn');
  fnLog.pause = true;

  fnLog.log('save to cache', { originalText, translatedText, sourceLang, targetLang });

  const store = await initCache();
  const key = generateCacheKey(originalText, sourceLang, targetLang);

  const cacheItem: TranslationCacheItem = {
    originalText,
    translatedText,
    sourceLang,
    targetLang,
    timestamp: Date.now(),
  };

  store.store.write(key, cacheItem);
  fnLog.log('saved to cache', { key, cacheItem });
}

/**
 * 清空所有缓存
 */
async function clearCache(): Promise<void> {
  const fnLog = fileLog.sub(false, 'clearCache_fn');
  fnLog.pause = true;

  fnLog.log('clearing all cache');

  const store = await initCache();
  store.store.clearAll();

  fnLog.log('cache cleared');
}

export const translationCache = {
  getFromCache,
  saveToCache,
  clearCache,
};
